import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { ReactNode } from "react";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { getSectionAccordionHeaderLabel } from "#get-section-accordion-header-label";
import {
  SECTION_ACCORDION_HEADER_ACTION_WIDTH,
  type SectionAccordionHeaderAction,
} from "#section-accordion-header-action";

export function SectionAccordionStickyHeader({
  actions,
  count,
  iconName,
  interactive = true,
  isLoading,
  isOpen,
  isPlaceholder,
  onToggle,
  title,
  trailing: trailingProp,
}: {
  /**
   * Toggles drawn at the trailing edge in place of the chevron. Inside a `SectionAccordionTab`
   * the native header owns their hit strips; standalone, each icon is its own pressable.
   */
  actions?: readonly SectionAccordionHeaderAction[];
  count?: number;
  iconName: IconName;
  /** The native accordion owns activation and accessibility for its single header view. */
  interactive?: boolean;
  /**
   * Swap the collapse/expand chevron for a small spinner — the standard section loading
   * affordance. Ignored when `trailing` is set. Owned here (not by callers) so every path that
   * renders this header — `SectionAccordionNodes` and the `SectionAccordionTab` reconstruction —
   * maps `isLoading` identically. Use for a BACKGROUND refetch over already-settled content: the
   * accessible label stays `"${title} section"`. For a header with NO real content behind it,
   * use `isPlaceholder` instead.
   */
  isLoading?: boolean;
  isOpen: boolean;
  /**
   * This header is a loading SKELETON — there is no real section content behind it (the Decks tab
   * redirect page and the deck suspense fallback render these). Implies the spinner, and swaps the
   * accessible label to `"Loading ${title}"` so a placeholder can never satisfy an assertion (or a
   * screen reader) looking for the real `"${title} section"`. That exact impersonation burned
   * dev-CI run 30924089209: word-sentence-drill's "Due Today section" gate matched the redirect
   * skeleton, and the flow then raced the redirect + suspense settle with a budget meant for
   * settled content. The wording is deliberate — the real label must NOT be a substring of the
   * placeholder label, or unanchored test matchers would still match it.
   */
  isPlaceholder?: boolean;
  onToggle: () => void;
  title: string;
  trailing?: ReactNode;
}): ReactNode {
  const trailing =
    trailingProp ??
    (isLoading === true || isPlaceholder === true ? <ActivityIndicator size="small" /> : undefined);
  const Container = interactive ? Pressable : View;

  return (
    <View className="border-b border-border bg-surface-header" collapsable={false}>
      <View className="mx-2.25">
        <Container
          accessibilityLabel={
            interactive
              ? getSectionAccordionHeaderLabel({ count, isPlaceholder, title })
              : undefined
          }
          accessibilityRole={interactive ? "button" : undefined}
          accessibilityState={
            interactive
              ? {
                  busy: isLoading === true || isPlaceholder === true,
                  expanded: isOpen,
                }
              : undefined
          }
          className="flex-row items-center justify-between py-2 active:bg-accent"
          onPress={interactive ? onToggle : undefined}
        >
          <View className="flex-1 flex-row items-center gap-2">
            <Icon className="text-muted-foreground" name={iconName} size={16} />
            <Text className="text-base leading-5 text-foreground">
              {title}
              {count === undefined ? null : (
                <Text className="text-base leading-5 text-muted-foreground"> ({count})</Text>
              )}
            </Text>
          </View>
          {trailing ??
            (actions === undefined ? (
              <Icon
                className="text-foreground"
                name={isOpen ? "LuChevronUp" : "LuChevronDown"}
                size={18}
              />
            ) : (
              <SectionAccordionHeaderActions actions={actions} interactive={interactive} />
            ))}
        </Container>
      </View>
    </View>
  );
}

/** Trailing toggles, each centered in its fixed-width strip (the native header's hit area). */
function SectionAccordionHeaderActions({
  actions,
  interactive,
}: {
  actions: readonly SectionAccordionHeaderAction[];
  interactive: boolean;
}) {
  return (
    <View className="flex-row">
      {actions.map((action) => {
        const icon = (
          <Icon
            className={action.selected ? "text-primary" : "text-muted-foreground"}
            name={action.iconName}
            size={18}
          />
        );
        return interactive ? (
          <Pressable
            accessibilityLabel={action.label}
            accessibilityRole="button"
            accessibilityState={{ selected: action.selected }}
            className="items-center"
            key={action.label}
            onPress={action.onPress}
            style={{ width: SECTION_ACCORDION_HEADER_ACTION_WIDTH }}
          >
            {icon}
          </Pressable>
        ) : (
          <View
            className="items-center"
            key={action.label}
            style={{ width: SECTION_ACCORDION_HEADER_ACTION_WIDTH }}
          >
            {icon}
          </View>
        );
      })}
    </View>
  );
}
